/**
 * Shared helpers for invoice Total / Due on Invoice Details (mirrors BE dealAmounts).
 */

export const INVOICE_SOURCE_DEBIT_NOTE = 'Debit Note';
export const INVOICE_SOURCE_CREDIT_NOTE = 'Credit Note';
export const INVOICE_SOURCE_EXTRA_CHARGE = 'Extra Charge';

export function isActiveInvoice(invoice) {
  return !invoice?.cancelled && invoice?.status !== 'Cancelled';
}

export function isCreditNoteInvoice(invoice) {
  return String(invoice?.source || '').trim() === INVOICE_SOURCE_CREDIT_NOTE;
}

export function isDebitNoteInvoice(invoice) {
  return String(invoice?.source || '').trim() === INVOICE_SOURCE_DEBIT_NOTE;
}

/** Late-fee invoices are additive dues, not part of the sale-price stage schedule. */
export function isLateFeeInvoice(invoice) {
  const source = String(invoice?.source || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  return source === 'latefee' || source === 'latefees';
}

export function invoiceOutstandingContribution(invoice) {
  if (!isActiveInvoice(invoice)) return 0;
  const amount = Number(invoice?.amount || 0);
  const lateRaw = Number(invoice?.late_fees || 0);
  const late = Number.isFinite(lateRaw) ? lateRaw : 0;
  const paid = Number(invoice?.paid_amount || 0);
  const paidLate = Number(invoice?.paid_late_fees || 0);
  if (isCreditNoteInvoice(invoice)) {
    return -amount;
  }
  return Math.max(0, amount + late - paid - paidLate);
}

export function invoiceTotalAdjustmentContribution(invoice) {
  if (!isActiveInvoice(invoice)) return 0;
  const amount = Number(invoice?.amount || 0);
  if (isCreditNoteInvoice(invoice)) return -amount;
  const source = String(invoice?.source || '').trim();
  if (source === INVOICE_SOURCE_DEBIT_NOTE || source === INVOICE_SOURCE_EXTRA_CHARGE) {
    return amount;
  }
  return 0;
}

/** Active invoice principal covering the sale schedule (excludes CN + latefee rows). */
export function invoiceSaleScheduleContribution(invoice) {
  if (!isActiveInvoice(invoice)) return 0;
  if (isCreditNoteInvoice(invoice)) return 0;
  if (isLateFeeInvoice(invoice)) return 0;
  return Math.max(0, Number(invoice?.amount || 0));
}

export function computeInvoiceOutstandingDue(invoices = []) {
  const due = invoices.reduce((sum, inv) => sum + invoiceOutstandingContribution(inv), 0);
  return Math.max(0, due);
}

export function computeUninvoicedSaleAmount(baseTotalPrice, invoices = []) {
  const adjustedTotal = computeAdjustedDealTotal(baseTotalPrice, invoices);
  const scheduled = invoices.reduce(
    (sum, inv) => sum + invoiceSaleScheduleContribution(inv),
    0
  );
  return Math.max(0, adjustedTotal - scheduled);
}

/**
 * Deal due vs sale: unpaid active invoices + uninvoiced sale remainder − credit.
 * Pass baseTotalPrice / creditBalance when available (Invoice Details / deal list).
 */
export function computeDealDueFromInvoices(invoices = [], options = {}) {
  const invoiceDue = computeInvoiceOutstandingDue(invoices);
  const baseTotal = options?.baseTotalPrice;
  if (baseTotal == null || !Number.isFinite(Number(baseTotal))) {
    return Math.max(0, Math.round(invoiceDue));
  }
  const uninvoiced = computeUninvoicedSaleAmount(Number(baseTotal), invoices);
  const credit = Math.max(0, Number(options?.creditBalance || 0));
  return Math.max(0, Math.round(invoiceDue + uninvoiced - credit));
}

/** Alias used by Invoice Details — same as computeDealDueFromInvoices with sale context. */
export function computeDealRemainingDue(
  baseTotalPrice,
  invoices = [],
  creditBalance = 0
) {
  return computeDealDueFromInvoices(invoices, {
    baseTotalPrice,
    creditBalance
  });
}

/** Paid principal + late fees on active invoices only (excludes cancelled / credit notes). */
export function computeDealPaidFromInvoices(invoices = []) {
  return invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || isCreditNoteInvoice(inv)) return sum;
    return (
      sum +
      Number(inv?.paid_amount || 0) +
      Number(inv?.paid_late_fees || 0)
    );
  }, 0);
}

export function computeAdjustedDealTotal(baseTotalPrice, invoices = []) {
  const adjustment = invoices.reduce(
    (sum, inv) => sum + invoiceTotalAdjustmentContribution(inv),
    0
  );
  return Math.max(0, Math.round(Number(baseTotalPrice || 0) + adjustment));
}

/**
 * Sale remaining only (excludes late-fee outstanding) — used for balance write-off.
 * = uninvoiced sale + unpaid stage principal − credit
 */
export function computeSaleRemainingBalance(
  baseTotalPrice,
  invoices = [],
  creditBalance = 0
) {
  const uninvoiced = computeUninvoicedSaleAmount(baseTotalPrice, invoices);
  const unpaidPrincipal = invoices.reduce((sum, inv) => {
    if (
      !isActiveInvoice(inv) ||
      isCreditNoteInvoice(inv) ||
      isLateFeeInvoice(inv)
    ) {
      return sum;
    }
    return sum + Math.max(0, Number(inv?.amount || 0) - Number(inv?.paid_amount || 0));
  }, 0);
  const credit = Math.max(0, Number(creditBalance || 0));
  return Math.max(0, Math.round(uninvoiced + unpaidPrincipal - credit));
}
